import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface CapTableData {
  name: string;
  percentage: number;
  shares: number;
  color: string;
}

interface CapTableSummaryProps {
  capTableData: CapTableData[];
  totalShares: number;
}

export const CapTableSummary = ({ capTableData, totalShares }: CapTableSummaryProps) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl text-primary">Cap Table Summary</CardTitle>
      </CardHeader>
      <CardContent className="space-y-8">
        <div className="text-center">
          <h3 className="text-lg font-semibold mb-2">Ownership Distribution</h3>
          <p className="text-sm text-gray-600 mb-4">Total Shares: {totalShares.toLocaleString()}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Pie Chart */}
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={capTableData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="percentage"
                >
                  {capTableData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number) => [`${value}%`, 'Ownership']}
                  contentStyle={{ backgroundColor: 'white', borderRadius: '8px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Stakeholder Cards */}
          <div className="grid grid-cols-2 gap-4">
            {capTableData.map((item, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow" style={{ borderColor: item.color }}>
                <CardContent className="p-4">
                  <h4 className="font-semibold mb-2" style={{ color: item.color }}>{item.name}</h4>
                  <div className="space-y-1">
                    <p className="text-2xl font-bold">{item.percentage}%</p>
                    <p className="text-sm text-gray-600">{item.shares.toLocaleString()} shares</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Detailed Table */}
        <div className="mt-8">
          <h3 className="text-lg font-semibold mb-4">Detailed Breakdown</h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Stakeholder</TableHead>
                <TableHead>Shares</TableHead>
                <TableHead>Percentage</TableHead>
                <TableHead>Agreements</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {capTableData.map((item, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell>{item.shares.toLocaleString()}</TableCell>
                  <TableCell>{item.percentage}%</TableCell>
                  <TableCell>
                    {item.name === 'Angel Investors' ? 'SAFE Notes' :
                     item.name === 'Anchor Investors' ? 'SAFE Notes' :
                     item.name === 'ESOP Pool' ? 'ESOP' :
                     item.name === 'Founders' ? 'Founders Agreement' : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="bg-muted p-4 rounded-lg mt-8">
          <h3 className="text-lg font-semibold mb-2">Notes</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li>• All shares are common stock with equal voting rights</li>
            <li>• ESOP pool is reserved for current and future employee incentives</li>
            <li>• Last valuation was conducted in Q4 2023</li>
            <li>• Fully diluted shares shown</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
